import { GridRowsProp } from '@mui/x-data-grid'
import { StationsReportSearchLog, StationsReportSearchLogRow } from '../interfaces'
import dayjs from 'dayjs'

// mapeia tipos de ação para rótulos em pt-BR
export function mapActionType(
  translate: (item: string) => string,
  requestType?: { readonly type?: 'CREATE' | 'EDIT' | 'DELETE' | string }
): string {
  const raw = String(requestType?.type || '').toUpperCase()

  if (raw === 'CREATE' || raw === 'POST') return translate('CREATE')
  if (raw === 'PUT' || raw === 'UPDATE') return translate('EDIT')
  if (raw === 'DELETE' || raw === 'REMOVED') return translate('DELETE')

  return raw ? raw : ''
}

export function buildReportTypeOptions(translate: (item: string) => string) {
  return [
    { value: 'create', label: translate('CREATE') },
    { value: 'edit', label: translate('EDIT') },
    { value: 'delete', label: translate('DELETE') },
  ]
}

const routeToTypeMap: Record<string, string> = {
  '/station/store': 'create',
  '/station/auth': 'activate_deactivate',
  '/station': 'edit', // Para PUT/PATCH
  '/dispatch/group/:id': 'edit',
  '/auth/login/change-password': 'change_password',
  '/user/force-disconnection': 'disconnect',
}

// Mapeamento considerando método HTTP
const routeMethodToTypeMap: Record<string, Record<string, string>> = {
  '/station': {
    'PUT': 'edit',
    'PATCH': 'edit', 
    'DELETE': 'delete',
    'GET': 'edit' // fallback para GET
  },
  '/station/:id': {
    'PUT': 'edit',
    'PATCH': 'edit',
    'DELETE': 'delete',
    'GET': 'edit' // fallback para GET
  }
}

export function mapRouteToReportTypeLabel(
  translate: (item: string) => string,
  route: string,
  method?: string
): string {
  // Normalize route com parâmetros dinâmicos (substitui números por :id)
  const normalizedRoute = route.replace(/\/(\d+)(?=\/|$)/g, '/:id')
  
  // Primeiro tenta mapear considerando o método HTTP
  if (method && routeMethodToTypeMap[normalizedRoute]) {
    const methodMap = routeMethodToTypeMap[normalizedRoute]
    const type = methodMap[method.toUpperCase()]
    if (type) {
      const options = buildReportTypeOptions(translate)
      const option = options.find((opt) => opt.value === type)
      return option ? option.label : route
    }
  }
  
  // Fallback para o mapeamento simples por rota
  const type = routeToTypeMap[normalizedRoute]
  if (type) {
    const options = buildReportTypeOptions(translate)
    const option = options.find((opt) => opt.value === type)
    return option ? option.label : route
  }
  
  return route
}

// Função para extrair datas do content
export const extractDatesFromContent = (content: string) => {
  try {
    const parsedContent = JSON.parse(content)
    return {
      start_date: parsedContent.start_date || null,
      finish_date: parsedContent.finish_date || null,
    }
  } catch {
    return {
      start_date: null,
      finish_date: null,
    }
  }
}

// Função dedicada para mapear os dados das linhas, separando responsabilidade
export const mapStationsReportRows = (
  data: readonly StationsReportSearchLog[],
  page: number,
  pageSize: number,
  translate: (key: string) => string
): GridRowsProp => {
  return data.map((element: StationsReportSearchLog, index: number) => {
    const generatedId = element.id ?? page * pageSize + index + 1
    // Passa o request_type (método HTTP) para a função de mapeamento
    const reportName = element.route
      ? mapRouteToReportTypeLabel(translate, element.route, element.request_type)
      : translate('Unknown Report')

    // action_display agora é derivado apenas da rota (reportName)
    const actionLabel = reportName

    const { start_date: contentStartDate, finish_date: contentFinishDate } =
      element.content
        ? extractDatesFromContent(element.content)
        : { start_date: null, finish_date: null }

    return {
      id: generatedId,
      ...element,
      'user:representation_name': element?.user?.name || '',
      'station:representation_name': element?.station?.name || '',
      report_display: reportName,
      action_display: actionLabel,
      origin: element?.station
        ? `Estação - ${element?.station}`
        : element?.user
        ? `user - ${element?.user}`
        : '',
      content_start_date: contentStartDate,
      content_finish_date: contentFinishDate,
      content_period:
        contentStartDate || contentFinishDate
          ? `${
              contentStartDate
                ? dayjs(contentStartDate).format('DD/MM/YYYY HH:mm')
                : ''
            }${contentStartDate && contentFinishDate ? ' - ' : ''}${
              contentFinishDate
                ? dayjs(contentFinishDate).format('DD/MM/YYYY HH:mm')
                : ''
            }`
          : null,
    } as StationsReportSearchLogRow
  })
}